/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useUpdateEffect } from 'ahooks';
import { Button, Dropdown, Flex, Modal, notification } from 'antd';

import {
  AntdRouterLink,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteRole } from 'services';
import type { RoleSummary } from 'types';
import { adjustPageNumber, generateTableDataSource, PATHNAME } from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    ROLES: { UPDATE_URL: ROLES_UPDATE_URL },
  },
} = PATHNAME;

type RolesTableProps = {
  loading: boolean;
  total: number;
  roles: RoleSummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const RolesTable = React.memo<RolesTableProps>(
  ({
    loading,
    total,
    roles,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteRole = useLockFn(async ({ id }: RoleSummary) => {
      deleteRole({ id })
        .then(() => {
          handleRefreshData();
          notification.success({
            message: 'دسترسی مورد نظر با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        });
    });

    const rolesDataSource = generateTableDataSource(roles, 'id');

    return (
      <PrimitiveTable
        columns={[
          {
            title: 'نام',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            render: (_, { id, name }) => (
              <AntdRouterLink
                ellipsis
                to={`${ROLES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
              >
                {name}
              </AntdRouterLink>
            ),
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'update-role',
                          label: (
                            <AntdRouterLink
                              to={`${ROLES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                ویرایش دسترسی
                              </span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-role',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف دسترسی مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteRole(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف دسترسی</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={rolesDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
RolesTable.displayName = 'RolesTable';

export { RolesTable };
